import { RumEvent } from '@datadog/browser-rum';

interface EventView {
  url: string;
  referrer?: string;
}

type OtherEventType = Exclude<
  RumEvent['type'],
  'resource' | 'error' | 'action'
>;

// The fields redaction touches; every RumEvent is assignable to it.
export type RedactableRumEvent =
  | { type: 'resource'; view: EventView; resource: { url: string } }
  | {
      type: 'error';
      view: EventView;
      error: { message: string; stack?: string; resource?: { url: string } };
    }
  | {
      type: 'action';
      view: EventView;
      action: { target?: { name: string } };
    }
  | { type: OtherEventType; view: EventView };

const REDACTED = 'REDACTED';
const URL_BASE_FOR_RELATIVE = 'https://relative.invalid';

// Query keys accountId, accountNo and accountNumber, in any case.
const ACCOUNT_QUERY_KEY = /^account(id|no|number)$/i;
// A whole path segment that is a numeric id (account, vehicle, direct debit) or
// an account number such as CC000013 (two letters, six digits, either case,
// as in the shared account regex).
const IDENTIFIER_SEGMENT = /^(\d+|[A-Za-z]{2}\d{6})$/;
// The same identifiers inside free text; four digits or more so status codes survive.
const IDENTIFIER_IN_TEXT = /\b([A-Za-z]{2}\d{6}|\d{4,})\b/g;
// Anything shaped like an email address: no spaces, one @, a dot in the domain.
const EMAIL_IN_TEXT = /[^\s@]+@[^\s@]+\.[^\s@]+/g;
// RUM formats every stack frame as "\n  at <function> @ <url>:<line>:<column>".
const STACK_FRAMES_START = '\n  at ';

export const redactText = (text: string): string =>
  text.replace(EMAIL_IN_TEXT, REDACTED).replace(IDENTIFIER_IN_TEXT, REDACTED);

// Only the header before the frames is redacted, because the frames' minified
// column numbers are long digit runs that sourcemaps need.
const redactStack = (stack: string): string => {
  const framesStart = stack.indexOf(STACK_FRAMES_START);
  if (framesStart === -1) {
    return redactText(stack);
  }
  return `${redactText(stack.slice(0, framesStart))}${stack.slice(
    framesStart
  )}`;
};

export const redactUrl = (url: string): string => {
  let parsed: URL;
  try {
    parsed = new URL(url, URL_BASE_FOR_RELATIVE);
  } catch {
    return redactText(url);
  }

  parsed.pathname = parsed.pathname
    .split('/')
    .map((segment) => (IDENTIFIER_SEGMENT.test(segment) ? REDACTED : segment))
    .join('/');
  [...parsed.searchParams.keys()]
    .filter((key) => ACCOUNT_QUERY_KEY.test(key))
    .forEach((key) => parsed.searchParams.set(key, REDACTED));

  return parsed.origin === URL_BASE_FOR_RELATIVE
    ? `${parsed.pathname}${parsed.search}${parsed.hash}`
    : parsed.toString();
};

const redactErrorEvent = (event: RedactableRumEvent): void => {
  if (event.type !== 'error') {
    return;
  }

  const { message, stack, resource } = event.error;
  event.error.message = redactText(message);
  event.error.stack = stack && redactStack(stack);
  if (resource) {
    resource.url = redactUrl(resource.url);
  }
};

export const redactRumEvent = (event: RedactableRumEvent): boolean => {
  event.view.url = redactUrl(event.view.url);
  if (event.view.referrer) {
    event.view.referrer = redactUrl(event.view.referrer);
  }
  if (event.type === 'resource') {
    event.resource.url = redactUrl(event.resource.url);
  }
  if (event.type === 'action' && event.action.target) {
    event.action.target.name = redactText(event.action.target.name);
  }
  redactErrorEvent(event);
  return true;
};
